'use client'

import { usePathname } from 'next/navigation'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

type ProductDetailProviderProps = {
  children: React.ReactNode
  product: Product | null
  contentQuantity: ITierPrices[]
  colorParam?: string | null
  sizeParam?: string | null
}

export type CommonTypeSelectedProduct = IVariantsProduct | undefined | null
interface ProductDetailContextType {
  isOpenPopup: boolean
  handleClose: () => void
  handleOpen: () => void
  productSelected: CommonTypeSelectedProduct
  setProductSelected: (data: CommonTypeSelectedProduct) => void
  product: Product | null
  // updateProduct: (data: Product) => void
  quantity: number
  setQuantity: (num: number) => void
  getAvailableOptions: (attributeCode: string) => {
    uid: string
    label: string
    hasStock: boolean
    image: string
    swatch_data?: {
      value: string
    }
  }[]
  handleOptionChange: (
    attributeCode: string,
    uid: string,
    callback?: (
      productToAdd: {
        product:
          | IVariantsProduct
          | {
              product: Product | null
            }
          | undefined
        options: Record<string, string>
        parent: Product | null
      }[]
    ) => void
  ) => void
  selectedOptions: Record<string, string>
  selectedVariant: IVariantsProduct | null
  sortedOptions: IConfigurableOptions[]
  convertedProductSelectedData:
    | Product
    | IVariantsProduct
    | {
        product: Product | null
      }
    | undefined
  productAddToCart: {
    product:
      | IVariantsProduct
      | {
          product: Product | null
        }
      | undefined
    options: Record<string, string>
    parent: Product | null
  }[]
  tempQuantityWithCoupon: IVariantsProduct['product']['tier_prices'] | undefined
  getShirtBackByColor: (colorUid: string) => string | null
  getCurrentShirtBack: () => string | null
}

const ProductDetailContext = createContext<ProductDetailContextType | undefined>(undefined)
const STOCK_STATUS = {
  in_stock: 'IN_STOCK',
  out_of_stock: 'OUT_OF_STOCK',
}
export const ProductDetailV2Provider = ({
  children,
  product,
  contentQuantity,
  colorParam,
  sizeParam,
}: ProductDetailProviderProps) => {
  const [productSelected, setProductSelected] = useState<CommonTypeSelectedProduct>()
  // const [product, setProduct] = useState<Product | null>(null)
  const [isOpenPopup, setIsOpenPopup] = useState<boolean>(false)
  const [quantity, setQuantity] = useState<number>(1)
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({})
  const [selectedVariant, setSelectedVariant] = useState<IVariantsProduct | null>(null)
  const pathname = usePathname()
  const isSelectedByURL = useMemo(() => {
    const segments = pathname.split('/')
    return segments.length > 0 ? segments[segments.length - 1] : ''
  }, [pathname])
  const productSelectedByURL = useMemo(
    () => product?.variants?.find((el) => el?.product?.url_key == isSelectedByURL),
    [product, isSelectedByURL]
  )
  const defaultProduct: IVariantsProduct = useMemo(
    () =>
      product?.variants?.find((it) => it?.product?.stock_status === 'IN_STOCK') ||
      product?.variants?.[0] ||
      ({} as any),
    [product?.variants]
  )

  const handleClose = () => {
    setIsOpenPopup(false)
  }
  const handleOpen = () => {
    setIsOpenPopup(true)
  }
  // const updateProduct = (data: Product) => {
  //   setProduct(data)
  // }

  // Sắp xếp configurable_options theo position
  const sortedOptions = useMemo(() => {
    return [...(product?.configurable_options || [])].sort((a, b) => a.position - b.position)
  }, [product?.configurable_options])

  // Tìm variant khớp với options đã chọn
  const findMatchingVariant = useCallback(
    (options: Record<string, string>) => {
      return product?.variants?.find((variant) => {
        return variant.attributes.every((attr) => {
          return options[attr.code] === attr.uid
        })
      })
    },
    [product?.variants]
  )

  const getAvailableOptions = (
    attributeCode: string
  ): { uid: string; label: string; hasStock: boolean; image: string }[] => {
    if (!product?.variants || !product?.configurable_options) return []
    const currentOption = sortedOptions.find((opt) => opt.attribute_code === attributeCode)
    if (!currentOption) return []
    // Lọc variants dựa trên TẤT CẢ các selections khác (không phân biệt position)
    const otherSelections: Record<string, any> = {}
    Object.entries(selectedOptions).forEach(([code, uid]) => {
      if (code !== attributeCode) {
        otherSelections[code] = uid
      }
    })

    // Tìm variants khớp với other selections
    const matchingVariants = product?.variants?.filter((variant) => {
      return Object.entries(otherSelections).every(([code, uid]) => {
        return variant.attributes.some((attr) => attr.code === code && attr.uid === uid)
      })
    })

    // Lấy unique values cho attribute code này
    const availableValues = new Map()
    matchingVariants.forEach((variant) => {
      const attr = variant.attributes.find((a) => a.code === attributeCode)
      if (attr) {
        const isInStock = variant.product.stock_status === STOCK_STATUS.in_stock
        const existing = availableValues.get(attr.uid)
        const optionValue = currentOption.values.find((v) => v.uid === attr.uid)
        availableValues.set(attr.uid, {
          uid: attr.uid,
          label: attr.label,
          hasStock: existing ? existing.hasStock || isInStock : isInStock,
          image: variant?.product?.image?.url || '',
          swatch_data: optionValue?.swatch_data || null,
        })
      }
    })

    // Sắp xếp theo thứ tự trong configurable_options.values
    const orderedValues = currentOption.values
      .map((optionValue) => availableValues.get(optionValue.uid))
      .filter(Boolean) // Loại bỏ undefined values

    return orderedValues
  }

  // Khởi tạo với variant đầu tiên còn hàng - CHỈ CHẠY 1 LẦN
  useEffect(() => {
    let tempVariant = productSelectedByURL
    const variants = product?.variants || []

    const hasColor = Boolean(colorParam)
    const hasSize = Boolean(sizeParam)

    const matchAttr = (v: IVariantsProduct, code: string, uid: string) =>
      v.attributes.find((attr) => attr.code === code)?.uid === uid

    if (!tempVariant && variants.length) {
      // 1️⃣ Ưu tiên match CẢ color + size và CÒN HÀNG
      if (hasColor && hasSize) {
        tempVariant = variants.find(
          (v) =>
            matchAttr(v, 'color', colorParam || '') &&
            matchAttr(v, 'size', sizeParam || '') &&
            v.product.stock_status === STOCK_STATUS.in_stock
        )

        // 2️⃣ Match CẢ color + size (bất kể tồn kho)
        if (!tempVariant) {
          tempVariant = variants.find(
            (v) => matchAttr(v, 'color', colorParam || '') && matchAttr(v, 'size', sizeParam || '')
          )
        }
      }

      // 3️⃣ Chỉ color → còn hàng
      if (!tempVariant && hasColor) {
        tempVariant = variants.find(
          (v) => matchAttr(v, 'color', colorParam || '') && v.product.stock_status === STOCK_STATUS.in_stock
        )

        if (!tempVariant) {
          tempVariant = variants.find((v) => matchAttr(v, 'color', colorParam || ''))
        }
      }

      // 4️⃣ Chỉ size → còn hàng
      if (!tempVariant && hasSize) {
        tempVariant = variants.find(
          (v) => matchAttr(v, 'size', sizeParam || '') && v.product.stock_status === STOCK_STATUS.in_stock
        )

        if (!tempVariant) {
          tempVariant = variants.find((v) => matchAttr(v, 'size', sizeParam || ''))
        }
      }
    }

    // 5️⃣ Fallback: variant đầu tiên còn hàng
    if (!tempVariant) {
      tempVariant = variants.find((v) => v.product.stock_status === STOCK_STATUS.in_stock)
    }

    if (tempVariant) {
      const initialOptions: Record<string, string> = {}

      tempVariant.attributes.forEach((attr) => {
        initialOptions[attr.code] = attr.uid
      })
      setSelectedOptions(initialOptions)
      setSelectedVariant(tempVariant)
    }
  }, [productSelectedByURL, product, colorParam, sizeParam])

  // Update variant khi options thay đổi
  useEffect(() => {
    // Kiểm tra xem đã chọn đủ tất cả options chưa
    const allOptionsSelected = sortedOptions.every((opt) => selectedOptions[opt.attribute_code])
    if (allOptionsSelected) {
      const variant = findMatchingVariant(selectedOptions)
      setSelectedVariant(variant || null)
    } else {
      // Nếu chưa đủ options, không set variant nhưng KHÔNG reset selections
      setSelectedVariant(null)
    }
  }, [selectedOptions, sortedOptions, findMatchingVariant])

  useEffect(() => {
    if (selectedVariant) {
      setProductSelected(selectedVariant)
    }
  }, [selectedVariant])

  const handleOptionChange = (
    attributeCode: string,
    uid: string,
    callback?: (
      productToAdd: {
        product:
          | IVariantsProduct
          | {
              product: Product | null
            }
          | undefined
        options: Record<string, string>
        parent: Product | null
      }[]
    ) => void
  ) => {
    const newOptions = { ...selectedOptions, [attributeCode]: uid }
    setSelectedOptions(newOptions)

    // Nếu có callback, tính toán productToAdd và gọi callback ngay lập tức
    if (callback) {
      // Tìm variant phù hợp với options mới
      const matchedVariant = product?.variants?.find((variant) => {
        return Object.entries(newOptions).every(([code, optionUid]) => {
          return variant?.attributes?.some((attr) => attr?.code === code && attr?.uid === optionUid)
        })
      })

      // Lấy product data - ưu tiên matchedVariant, fallback sang selectedVariant
      const productData = matchedVariant || selectedVariant

      // Chỉ gọi callback nếu có productData hợp lệ
      if (productData) {
        const productToAdd = [
          {
            product: productData,
            options: newOptions,
            parent: product,
          },
        ]

        // Gọi callback với product đã tính toán
        callback(productToAdd)
      }
    }
  }

  const convertedProductSelectedData = useMemo(() => {
    try {
      if (productSelected) {
        return productSelected
      }
      if (Object.keys(defaultProduct).length > 0) {
        return defaultProduct
      }
      return { product: product }
    } catch (error) {}
  }, [product, defaultProduct, productSelected])

  const productAddToCart = useMemo(() => {
    return [
      {
        product: convertedProductSelectedData,
        options: selectedOptions,
        parent: product,
      },
    ]
  }, [convertedProductSelectedData, product, selectedOptions])

  const getShirtBackByColor = useCallback(
    (colorUid: string): string | null => {
      if (!product?.variants) return null

      // Find any variant with the specified color that has shirt_back
      const variantWithShirtBack = product?.variants?.find((variant) => {
        const colorAttr = variant?.attributes?.find((attr) => attr?.code === 'color')
        return colorAttr?.uid === colorUid && variant?.product?.shirt_back
      })

      return variantWithShirtBack?.product?.shirt_back || null
    },
    [product?.variants]
  )

  const getCurrentShirtBack = useCallback((): string | null => {
    if (!selectedOptions.color) return null
    return getShirtBackByColor(selectedOptions.color)
  }, [selectedOptions.color, getShirtBackByColor])

  return (
    <ProductDetailContext.Provider
      value={{
        productSelected,
        setProductSelected,
        isOpenPopup,
        handleClose,
        handleOpen,
        product,
        // updateProduct,
        quantity,
        setQuantity,
        getAvailableOptions,
        handleOptionChange,
        selectedOptions,
        selectedVariant,
        sortedOptions,
        convertedProductSelectedData,
        productAddToCart,
        tempQuantityWithCoupon: contentQuantity,
        getShirtBackByColor,
        getCurrentShirtBack,
      }}
    >
      {children}
    </ProductDetailContext.Provider>
  )
}

export const useProductDetailV2State = () => {
  const productContext = useContext(ProductDetailContext)
  if (productContext === undefined) {
    return {} as ProductDetailContextType
  }
  return productContext
}
